/**
 * §7.4 進庫（交接）。設備團隊作業流程的 1.0 交接。
 *
 * 與 mockup 的差異：**移除「示範捷徑：批次補齊」**（§7.4）。
 * 那是 demo 用的——正式站台上批次生成面板資料等於偽造建檔紀錄。
 *
 * 三步：登記送貨單 → 逐片建檔 → 進庫過帳。
 * §9 規則 11 在最後一步會出現：RCV-IN-01 停用時，進庫過帳沒有重量，
 * 系統照過，但會講清楚後果。
 */

import { useEffect, useMemo, useState } from 'react'
import { ApiError, api } from '../api/client.js'
import { useMutation, useQuery } from '../api/hooks.js'
import { Alert, Button, Card, Empty, Field, FieldLabelSpacer, KV, Mono, Pill, Select, Steps, Table, Td, TextInput } from '../components/ui.js'
import { ModelFormModal } from '../components/ModelForm.js'
import { useAuth } from '../auth/AuthContext.js'
import { useConfig, useDict } from '../config/ConfigContext.js'
import { count, modelOptionLabel } from '../../../shared/lib/format.js'
import { Link } from '../router.js'
import { verifyId } from '../../../shared/lib/checkdigit.js'

interface PoolState {
  counts: { total: number; available: number; reserved: number; used: number; voided: number }
  ids: { id: string; status: string; reservation_id: string | null }[]
  reservations: { reservation_id: string; device_id: string; reserved_at: string; status: string; total: string; unused: string }[]
}

interface PanelDraft {
  panelId: string
  modelCode: string
  regulatoryId: string
  conditionGrade: 'A' | 'B' | 'C' | 'damaged'
  moisture: 'dry' | 'damp' | 'wet'
  rescanVerified: boolean
}

export function Receiving() {
  const { config } = useConfig()
  const dict = useDict()
  const [step, setStep] = useState(0)
  const [delivery, setDelivery] = useState<{ deliveryId: string; declaredCount: number; customer: string } | null>(null)
  const [drafts, setDrafts] = useState<PanelDraft[]>([])
  const [notice, setNotice] = useState<string | null>(null)

  const pool = useQuery<PoolState>('/api/id-pool?idType=panel&reservedOnly=true&limit=1000')

  return (
    <div className="space-y-4">
      <Steps steps={['登記送貨單', '逐片建檔', '進庫過帳']} current={step} />
      {notice && <Alert tone="ok">{notice}</Alert>}

      {step === 0 && (
        <DeliveryStep
          config={config}
          onDone={(d) => { setDelivery(d); setStep(1) }}
        />
      )}

      {step === 1 && delivery && (
        <RegisterStep
          delivery={delivery}
          pool={pool.data}
          poolLoading={pool.loading}
          drafts={drafts}
          setDrafts={setDrafts}
          config={config}
          onBack={() => setStep(0)}
          onDone={(n) => {
            setNotice(`已建檔 ${count(n)} 片。接著把它們過帳入庫。`)
            pool.reload()
            setStep(2)
          }}
        />
      )}

      {step === 2 && delivery && (
        <PostStep
          delivery={delivery}
          panelIds={drafts.map((d) => d.panelId)}
          config={config}
          onDone={(itemId) => {
            setNotice(`進庫完成，料件 ${itemId}。`)
            setDrafts([])
            setDelivery(null)
            setStep(0)
          }}
        />
      )}
      {void dict}
    </div>
  )
}

// ── Step 1：登記送貨單 ─────────────────────────────────────────────────────

function DeliveryStep({ config, onDone }: {
  config: ReturnType<typeof useConfig>['config']
  onDone: (d: { deliveryId: string; declaredCount: number; customer: string }) => void
}) {
  const [declaredCount, setDeclaredCount] = useState('')
  const [customer, setCustomer] = useState('')
  const [note, setNote] = useState('')

  const post = useMutation<void, { deliveryId: string }>(async (_a, key) =>
    api.post('/api/deliveries', {
      declaredCount: Number(declaredCount), customer, note,
    }, { idempotencyKey: key }))

  useEffect(() => {
    if (!customer && config?.customers?.[0]) setCustomer(config.customers[0].customerId)
  }, [config, customer])

  return (
    <Card title="Step 1 — 登記送貨單" subtitle="設備團隊作業流程 1.0 交接">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="申報片數" hint="來料單上寫的數量" required>
          <TextInput inputMode="numeric" value={declaredCount}
            onChange={(e) => setDeclaredCount(e.target.value.replace(/\D/g, ''))} />
        </Field>
        <Field label="來源客戶" required>
          <Select value={customer} onChange={(e) => setCustomer(e.target.value)}>
            {(config?.customers ?? []).map((c) => (
              <option key={c.customerId} value={c.customerId}>{c.label}</option>
            ))}
          </Select>
        </Field>
        <Field label="備註">
          <TextInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="選填" />
        </Field>
      </div>

      {post.error && (
        <div className="mt-3">
          <Alert tone="danger" title="登記失敗">{post.error.message}</Alert>
        </div>
      )}

      <div className="mt-4">
        <Button
          variant="primary" size="lg"
          disabled={!declaredCount || !customer || post.pending}
          onClick={async () => {
            const r = await post.run()
            if (r) onDone({ deliveryId: r.deliveryId, declaredCount: Number(declaredCount), customer })
          }}
        >{post.pending ? '登記中…' : '登記並開始建檔'}</Button>
      </div>
    </Card>
  )
}

// ── Step 2：逐片建檔 ───────────────────────────────────────────────────────

function RegisterStep({ delivery, pool, poolLoading, drafts, setDrafts, config, onBack, onDone }: {
  delivery: { deliveryId: string; declaredCount: number; customer: string }
  pool: PoolState | null | undefined
  poolLoading: boolean
  drafts: PanelDraft[]
  setDrafts: (d: PanelDraft[]) => void
  config: ReturnType<typeof useConfig>['config']
  onBack: () => void
  onDone: (n: number) => void
}) {
  const [scan, setScan] = useState('')
  const [modelCode, setModelCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [addingModel, setAddingModel] = useState(false)
  const [modelNotice, setModelNotice] = useState<string | null>(null)
  const { reload: reloadConfig } = useConfig()
  const { can } = useAuth()

  const reserved = useMemo(
    () => (pool?.ids ?? []).filter((x) => x.status === 'reserved').map((x) => x.id),
    [pool],
  )
  const used = useMemo(() => new Set(drafts.map((d) => d.panelId)), [drafts])
  const nextId = reserved.find((id) => !used.has(id)) ?? null

  useEffect(() => {
    if (!modelCode && config?.modelSpecs?.[0]) setModelCode(config.modelSpecs[0].modelCode)
  }, [config, modelCode])

  const post = useMutation<void, { registered: number }>(async (_a, key) =>
    api.post('/api/panels', {
      deliveryId: delivery.deliveryId,
      panels: drafts.map((d) => ({
        panelId: d.panelId,
        modelCode: d.modelCode || null,
        regulatoryId: d.regulatoryId || null,
        conditionGrade: d.conditionGrade,
        moisture: d.moisture,
        sourceCustomer: delivery.customer,
        rescanVerified: d.rescanVerified,
      })),
    }, { idempotencyKey: key }))

  const add = (panelId: string) => {
    const id = panelId.trim()
    if (!id) return
    if (used.has(id)) { setError(`${id} 已經在這一批裡了。`); return }
    if (!reserved.includes(id)) {
      setError(`${id} 不在本裝置預提的號碼裡。請確認掃到的標籤，或請主管預提號碼。`)
      return
    }
    setError(null)
    setDrafts([...drafts, {
      panelId: id, modelCode, regulatoryId: '',
      conditionGrade: 'A', moisture: 'dry',
      // 回掃驗證：掃進來的號碼與預提的號碼相符
      rescanVerified: verifyId(id),
    }])
    setScan('')
  }

  return (
    <>
      <Card title="Step 2 — 逐片建檔" subtitle={`送貨單 ${delivery.deliveryId} · 申報 ${count(delivery.declaredCount)} 片`}>
        {poolLoading && <p className="text-sm text-ink-500">讀取預提號碼…</p>}

        {!poolLoading && reserved.length === 0 && (
          <Alert tone="warn" title="本裝置沒有預提的 PV 號">
            進庫建檔要用預提好的號碼（標籤已經印出來、貼在板子上）。
            請主管到<Link to="/idpool" className="mx-1 underline">號碼池</Link>預提一批給這台裝置。
          </Alert>
        )}

        {reserved.length > 0 && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
              <span>預提可用 <strong className="tabular-nums">{count(reserved.length - used.size)}</strong> 個</span>
              <span className="text-ink-400">·</span>
              <span>已建檔 <strong className="tabular-nums">{count(drafts.length)}</strong> 片</span>
              {drafts.length !== delivery.declaredCount && drafts.length > 0 && (
                <Pill tone="amber" title="不阻擋，但要知道">
                  與申報數 {count(delivery.declaredCount)} 不符
                </Pill>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-[1fr,200px,auto]">
              <Field label="掃描 PV 號" hint={nextId ? `下一個預提號碼：${nextId}` : '預提的號碼都用完了'}>
                <TextInput
                  value={scan}
                  autoFocus
                  spellCheck={false}
                  className="font-mono"
                  placeholder={nextId ?? ''}
                  onChange={(e) => setScan(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(scan) } }}
                />
              </Field>
              {/*
                「＋ 新增型號」放在 Field **外面**：Field 渲染的是 <label>，
                把 <button> 包進 label 裡，點按鈕會連帶觸發 label 去操作那個 <select>。
              */}
              <div>
                <Field label="型號" hint="沒見過的型號按下面的「＋ 新增」，不要選未辨識">
                  <Select value={modelCode} onChange={(e) => setModelCode(e.target.value)}>
                    <option value="">未辨識</option>
                    {(config?.modelSpecs ?? []).map((m) => (
                      <option key={m.modelCode} value={m.modelCode}>
                        {modelOptionLabel(m)}
                      </option>
                    ))}
                  </Select>
                </Field>
                {/*
                  現場遇到沒見過的型號，原本只有一條路：選「未辨識」。
                  那等於把型號資訊丟掉，事後沒有人補得回來。
                  這顆按鈕讓他當場建一個暫定型號——不必離開這個畫面、不必等主管。
                */}
                <button
                  type="button"
                  onClick={() => setAddingModel(true)}
                  className="mt-1 text-xs text-sky-700 underline"
                >＋ 新增型號（找不到這批的型號時按這裡）</button>
              </div>
              {/* 按鈕與輸入框對齊：見 FieldLabelSpacer 的說明。
                  這一格比左邊兩格矮，用 items-end 會被拉到整列的底部 */}
              <div className="self-start">
                <FieldLabelSpacer />
                <div className="flex gap-2">
                  <Button size="lg" onClick={() => add(scan)} disabled={!scan.trim()}>加入</Button>
                  <Button size="lg" variant="primary" disabled={!nextId}
                    onClick={() => nextId && add(nextId)}>用下一個號碼</Button>
                </div>
              </div>
            </div>

            {error && <div className="mt-2"><Alert tone="danger">{error}</Alert></div>}
            {modelNotice && <div className="mt-2"><Alert tone="ok" title="型號已建立">{modelNotice}</Alert></div>}
          </>
        )}
      </Card>

      {addingModel && (
        <ModelFormModal
          mode="create"
          existing={(config?.modelSpecs ?? []).map((m) => m.modelCode)}
          canConfirm={can('settings.dictionary.write')}
          onClose={() => setAddingModel(false)}
          onDone={(msg) => {
            setAddingModel(false)
            setModelNotice(msg)
            // 下拉要立刻看得到新型號，而且直接選起來——現場不該再點一次
            void reloadConfig()
          }}
        />
      )}

      {drafts.length > 0 && (
        <Card title={`已建檔 ${count(drafts.length)} 片`} subtitle="送出前可以修改每一片的資料">
          <div className="max-h-[24rem] overflow-y-auto">
            <Table head={['#', 'PV 號', '型號', '列管編碼', '外觀', '含水', '']} dense>
              {drafts.map((d, i) => (
                <tr key={d.panelId}>
                  <Td className="w-10 tabular-nums text-ink-400">{i + 1}</Td>
                  <Td><Mono>{d.panelId}</Mono></Td>
                  <Td className="w-[140px]">
                    <Select value={d.modelCode} className="text-xs"
                      onChange={(e) => setDrafts(drafts.map((x, j) => j === i ? { ...x, modelCode: e.target.value } : x))}>
                      <option value="">未辨識</option>
                      {(config?.modelSpecs ?? []).map((m) => (
                        <option key={m.modelCode} value={m.modelCode}>
                          {m.modelCode}{m.ratedW ? ` ${m.ratedW}W` : ''}{m.status === 'provisional' ? '〔暫定〕' : ''}
                        </option>
                      ))}
                    </Select>
                  </Td>
                  <Td className="w-[150px]">
                    <TextInput value={d.regulatoryId} placeholder="可空" className="font-mono text-xs"
                      onChange={(e) => setDrafts(drafts.map((x, j) => j === i ? { ...x, regulatoryId: e.target.value } : x))} />
                  </Td>
                  <Td className="w-[110px]">
                    <Select value={d.conditionGrade} className="text-xs"
                      onChange={(e) => setDrafts(drafts.map((x, j) => j === i
                        ? { ...x, conditionGrade: e.target.value as PanelDraft['conditionGrade'] } : x))}>
                      <option value="A">A</option><option value="B">B</option>
                      <option value="C">C</option><option value="damaged">破損</option>
                    </Select>
                  </Td>
                  <Td className="w-[100px]">
                    <Select value={d.moisture} className="text-xs"
                      onChange={(e) => setDrafts(drafts.map((x, j) => j === i
                        ? { ...x, moisture: e.target.value as PanelDraft['moisture'] } : x))}>
                      <option value="dry">乾</option><option value="damp">微濕</option><option value="wet">濕</option>
                    </Select>
                  </Td>
                  <Td>
                    <Button size="sm" variant="ghost"
                      onClick={() => setDrafts(drafts.filter((_, j) => j !== i))}>移除</Button>
                  </Td>
                </tr>
              ))}
            </Table>
          </div>

          {post.error && (
            <div className="mt-3"><Alert tone="danger" title="建檔失敗">{post.error.message}</Alert></div>
          )}

          <div className="mt-4 flex gap-2">
            <Button onClick={onBack}>上一步</Button>
            <Button variant="primary" size="lg" disabled={post.pending}
              onClick={async () => { const r = await post.run(); if (r) onDone(r.registered) }}>
              {post.pending ? '建檔中…' : `建檔 ${count(drafts.length)} 片`}
            </Button>
          </div>
        </Card>
      )}
    </>
  )
}

// ── Step 3：進庫過帳 ───────────────────────────────────────────────────────

function PostStep({ delivery, panelIds, config, onDone }: {
  delivery: { deliveryId: string }
  panelIds: string[]
  config: ReturnType<typeof useConfig>['config']
  onDone: (itemId: string) => void
}) {
  const dict = useDict()
  const pool = useQuery<PoolState>('/api/id-pool?idType=item&limit=50')
  const [itemId, setItemId] = useState('')
  const [containerId, setContainerId] = useState('')
  const [locationId, setLocationId] = useState('')

  // RCV-IN-01 是刻意保留的量測缺口（§9 規則 6）
  const mp = config?.measurementPoints?.find((m) => m.measurementPointId === 'RCV-IN-01') ?? null

  useEffect(() => {
    if (!itemId) {
      const first = (pool.data?.ids ?? []).find((x) => x.status === 'available' || x.status === 'reserved')
      if (first) setItemId(first.id)
    }
    if (!containerId && config?.containers?.[0]) setContainerId(config.containers[0].containerId)
    if (!locationId && config?.locations?.[0]) setLocationId(config.locations[0].locationId)
  }, [pool.data, config, itemId, containerId, locationId])

  const post = useMutation<void, { receiptOrderId: string; receiptLineIds: string[] }>(async (_a, key) =>
    api.post('/api/receipts', {
      workOrderId: null,
      lines: [{
        newItemId: itemId,
        materialCode: 'PANEL',
        containerId: containerId || null,
        locationId: locationId || null,
        panelIds,
        // 量測點停用時不帶 measurementPointId —— 沒有重量，且不硬湊（§9 規則 11）
        ...(mp?.enabled ? { measurementPointId: mp.measurementPointId } : {}),
      }],
    }, { idempotencyKey: key }))

  return (
    <Card title="Step 3 — 進庫過帳" subtitle={`送貨單 ${delivery.deliveryId} · ${count(panelIds.length)} 片`}>
      {/* §9 規則 6／11：刻意保留的量測缺口，要讓後果看得見 */}
      {mp && !mp.enabled && (
        <div className="mb-4">
          <Alert tone="warn" title="本批不會有重量帳">
            進庫過磅的量測點 <Mono>{mp.measurementPointId}</Mono> 目前是停用狀態（現場還沒有地磅）。
            這一批會照樣入庫，但料件的重量是空的，後續的影響是：
            <ul className="mt-1 ml-4 list-disc space-y-0.5">
              <li>全鏈平衡的分母改用 OP-1 的逐片實測合計，進庫到 OP-1 之間的損耗無法查核</li>
              <li>OP-2 之後若要「帳面繼承」重量會失敗，因為來源沒有重量可以繼承</li>
            </ul>
            系統不會替您把數字湊出來。要補上這一段，請主管到作業設定啟用這個量測點。
          </Alert>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="料件號" hint="從號碼池取一個" required>
          <Select value={itemId} onChange={(e) => setItemId(e.target.value)}>
            <option value="">請選擇</option>
            {(pool.data?.ids ?? []).map((x) => (
              <option key={x.id} value={x.id}>{x.id}</option>
            ))}
          </Select>
        </Field>
        <Field label="容器">
          <Select value={containerId} onChange={(e) => setContainerId(e.target.value)}>
            {(config?.containers ?? [])
              .filter((c) => !c.forMaterials || c.forMaterials.includes('PANEL'))
              .map((c) => <option key={c.containerId} value={c.containerId}>{c.label}</option>)}
          </Select>
        </Field>
        <Field label="儲位">
          <Select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            {(config?.locations ?? []).map((l) => (
              <option key={l.locationId} value={l.locationId}>{l.label}</option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="mt-4">
        <KV items={[
          ['物料', dict.material('PANEL')],
          ['片數', count(panelIds.length)],
          ['重量', mp?.enabled ? '（下一步輸入）' : '無（量測點停用）'],
        ]} />
      </div>

      {post.error && (
        <div className="mt-3"><Alert tone="danger" title="過帳失敗">{post.error.message}</Alert></div>
      )}

      <div className="mt-4">
        <Button variant="primary" size="lg" disabled={!itemId || post.pending}
          onClick={async () => { const r = await post.run(); if (r) onDone(itemId) }}>
          {post.pending ? '過帳中…' : '進庫過帳'}
        </Button>
      </div>

      {panelIds.length === 0 && <div className="mt-3"><Empty>這一批沒有任何面板。</Empty></div>}
    </Card>
  )
}
